import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { FrontendBlogSilo } from '../../../../core/api/api.models';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { stripHtml } from '../../blog-post.mapper';

const SILOS_PATH = '/blogs/hubs';
const TRENDING_COUNT = 4;
const FEATURED_SMALL_COUNT = 4;
const SIDEBAR_COUNT = 6;
const POPULAR_COUNT = 6;

interface TopicView {
  name: string;
  slug: string;
  description: string;
  totalBlogs: number;
  /** First letter of the topic — silos carry no icon in the admin, so nothing is invented here. */
  initial: string;
}

@Component({
  selector: 'app-blog-silos-hub-page',
  imports: [Breadcrumb, RouterLink],
  templateUrl: './blog-silos-hub-page.html',
  styleUrl: './blog-silos-hub-page.scss',
})
export class BlogSilosHubPage {
  protected readonly crumbs = breadcrumbTrail(
    { label: 'Blog', path: '/blogs' },
    { label: 'Topics' },
  );

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  protected readonly loaded = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly activeLetter = signal('');

  /** Every silo, richest first — the order the "featured" and "popular" sections read from. */
  private readonly topics = signal<TopicView[]>([]);

  protected readonly totalTopics = computed(() => this.topics().length);
  protected readonly totalGuides = computed(() =>
    this.topics().reduce((sum, topic) => sum + topic.totalBlogs, 0),
  );

  protected readonly trendingTopics = computed(() => this.topics().slice(0, TRENDING_COUNT));
  protected readonly featuredTopic = computed(() => this.topics()[0] ?? null);
  protected readonly featuredTopics = computed(() =>
    this.topics().slice(1, 1 + FEATURED_SMALL_COUNT),
  );
  protected readonly sidebarTopics = computed(() => this.topics().slice(0, SIDEBAR_COUNT));
  protected readonly popularTopics = computed(() =>
    this.topics().slice(1 + FEATURED_SMALL_COUNT, 1 + FEATURED_SMALL_COUNT + POPULAR_COUNT),
  );

  /** Alphabetical directory of every topic, narrowed by the search box and the A–Z strip. */
  protected readonly directoryTopics = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const letter = this.activeLetter();

    return this.topics()
      .filter((topic) => {
        const matchesTerm = !term || topic.name.toLowerCase().includes(term);
        const matchesLetter = !letter || topic.name.toUpperCase().startsWith(letter);
        return matchesTerm && matchesLetter;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  /** Letters are disabled when no topic starts with them, so the strip never leads to nothing. */
  protected readonly alphabet = computed(() => {
    const present = new Set(this.topics().map((topic) => topic.name.charAt(0).toUpperCase()));

    return Array.from({ length: 26 }, (_, index) => {
      const letter = String.fromCharCode(65 + index);
      return { letter, hasTopics: present.has(letter) };
    });
  });

  constructor() {
    this.loadSilos();
    this.applySeo();
  }

  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  protected toggleLetter(letter: string): void {
    this.activeLetter.update((current) => (current === letter ? '' : letter));
  }

  protected clearFilters(): void {
    this.searchTerm.set('');
    this.activeLetter.set('');
  }

  private loadSilos(page = 1, accumulated: FrontendBlogSilo[] = []): void {
    this.api
      .getBlogSilos({ page, limit: 100 })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) {
          this.setTopics(accumulated);
          return;
        }

        const silos = [...accumulated, ...response.data];

        // The directory lists every topic, so walk past the API's page cap when there are more.
        if (response.pagination.page < response.pagination.pages) {
          this.loadSilos(response.pagination.page + 1, silos);
          return;
        }

        this.setTopics(silos);
      });
  }

  private setTopics(silos: FrontendBlogSilo[]): void {
    this.topics.set(
      silos
        .map((silo) => this.toTopicView(silo))
        .filter((topic) => topic.slug)
        .sort((a, b) => b.totalBlogs - a.totalBlogs || a.name.localeCompare(b.name)),
    );
    this.loaded.set(true);
  }

  private toTopicView(silo: FrontendBlogSilo): TopicView {
    const name = (silo.name || '').trim();
    const description =
      stripHtml(String(silo.shortContent ?? '')) || stripHtml(String(silo.metaDescription ?? ''));

    return {
      name,
      slug: (silo.slug || '').trim(),
      description:
        description.length > 130 ? `${description.slice(0, 130).trimEnd()}...` : description,
      totalBlogs: Number(silo.totalBlogs ?? 0),
      initial: name.charAt(0).toUpperCase(),
    };
  }

  private applySeo(): void {
    const title = 'Savings Topic Hub | Browse Every SavingArena Guide Collection';
    const description =
      'Browse SavingArena topic hubs: coupon strategies, seasonal sale guides, retailer deal collections and expert savings content, organised in one place.';

    this.seo.apply({ title, description, canonicalPath: SILOS_PATH });
    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url: this.seo.absolute(SILOS_PATH),
        name: title,
        description,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }
}
